<template>
  <!-- Dispatch properties for one site: one row per property with its editor
       chosen by propertyType, and a single Save at the foot of the list. -->
  <div class="dispatchPane">
    <!-- A plain overflow container rather than DxScrollView: the DevExtreme one
         was not clamping its height here, so the content grew and pushed the
         Save button out of the pane. -->
    <div class="paneScroll">
      <div class="paneBody">
        <div v-if="!dispatchProps.length" class="emptyNote">
          None of the configured dispatch properties exist on this site.
        </div>

        <div v-else class="fieldRows">
          <div v-for="prop in dispatchProps" :key="prop.containerPropId" class="row"
            :class="{ dirty: isDirty(prop) }">
            <div class="fieldLabel rowLabel">
              <span>{{ prop.propertyName }}</span>
              <span v-if="isDirty(prop)" class="dirtyMark" title="Unsaved change">&bull;</span>
            </div>

            <!-- Editable: the control is chosen by propertyType. A percent
                 property also shows what its value comes to in litres, to the
                 left of the input. -->
            <div v-if="allowUserEdit(prop.propertyName)" class="rowControl">
              <span v-if="capacityHint(prop)" class="capacityHint">{{ capacityHint(prop) }}</span>

              <div class="editorWrap">
                <DxSwitch v-if="editorFor(prop) === 'boolean'" :value="edits[prop.containerPropId]"
                  switched-on-text="Yes" switched-off-text="No"
                  @value-changed="onValueChanged(prop, $event)" />

                <DxNumberBox v-else-if="editorFor(prop) === 'number'" :value="edits[prop.containerPropId]"
                  :format="prop.propertyType === 'NUMBER' ? '#0' : undefined" label="Value" label-mode="hidden"
                  :min="isPercentProp(prop) ? percentMin : undefined"
                  :max="isPercentProp(prop) ? percentMax : undefined"
                  @key-down="handleKeyDown" @value-changed="onValueChanged(prop, $event)" />

                <DxDateBox v-else-if="editorFor(prop) === 'date'" :value="edits[prop.containerPropId]" type="date"
                  display-format="yyyy-MM-dd" open-on-field-click="true" :show-clear-button="true"
                  @value-changed="onValueChanged(prop, $event)" />

                <DxTextBox v-else :value="edits[prop.containerPropId]" :max-length="50" label="Value"
                  label-mode="hidden" @value-changed="onValueChanged(prop, $event)" />
              </div>
            </div>

            <!-- Read-only: no userEdit on the property, or an operator is viewing. -->
            <div v-else class="rowControl">
              <span v-if="capacityHint(prop)" class="capacityHint">{{ capacityHint(prop) }}</span>
              <span class="readonlyVal">{{ prop.status }} {{ displayValue(prop) }}</span>
            </div>
          </div>
        </div>

        <!-- One Save for the whole form; each changed property is sent on its
             own. It sits at the foot of the properties and scrolls with them,
             rather than being pinned to the bottom of the pane. -->
        <div class="paneFooter">
          <span v-if="saveError" class="saveError">{{ saveError }}</span>
          <span v-else-if="dirtyProps.length" class="dirtyCount">
            {{ dirtyProps.length }} unsaved change{{ dirtyProps.length === 1 ? '' : 's' }}
          </span>
          <DxButton class="saveBtn" icon="save" :text="saving ? 'Saving...' : 'Save'"
            :disabled="!dirtyProps.length || saving" @click="saveChanges" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DxTextBox from "devextreme-vue/text-box";
import DxNumberBox from "devextreme-vue/number-box";
import DxDateBox from "devextreme-vue/date-box";
import DxSwitch from "devextreme-vue/switch";
import DxButton from "devextreme-vue/button";
// Imperative toast, as used by container-bulk-import.vue and the auth forms. It
// renders at document level, so it is not clipped by the DxPopup this pane sits
// inside the way a DxToast declared in this template would be.
import notify from "devextreme/ui/notify";
import { dateToString } from "@/utils/dateToString.js";

// The properties this pane shows, by exact propertyName, in display order.
const DISPATCH_PROPERTIES = ['Dispatch Enabled', 'Capacity', 'Product', 'Minimum Fill', 'Max Fill'];

// propValue is stored as a string, so a BOOLEAN comes back as text. There is no
// other BOOLEAN property in the app yet, so these are the forms accepted on the
// way in; '1'/'0' is what gets written back out (see serializeValue).
const TRUE_STRINGS = ['true', '1', 'yes', 'y', 't'];

// Properties held as a percentage of tank capacity: entry is clamped to
// PERCENT_MIN..PERCENT_MAX, and each shows what its percentage works out to in
// litres. Same meaning as maxFillPct in the dispatch store's fill forecast.
const PERCENT_OF_CAPACITY_PROPERTIES = ['Minimum Fill', 'Max Fill'];
const PERCENT_MIN = 1;
const PERCENT_MAX = 100;

// The property the percentages are taken against.
const CAPACITY_PROPERTY = 'Capacity';

export default {
  components: {
    DxTextBox,
    DxNumberBox,
    DxDateBox,
    DxSwitch,
    DxButton
  },
  props: ["containerId", "container", "isOperator"],
  created() {
    this.syncFromProps();
  },
  data() {
    return {
      dispatchPropertyNames: DISPATCH_PROPERTIES,
      // Working value per containerPropId, typed to suit its editor.
      edits: {},
      // Serialized value each property started at, so dirty state survives a
      // save without needing the store rows to refresh first.
      baseline: {},
      saving: false,
      saveError: null,
      // Exposed so the number box can bind them without magic numbers.
      percentMin: PERCENT_MIN,
      percentMax: PERCENT_MAX,
    };
  },
  computed: {
    // The container's own property rows, narrowed to the configured names and
    // ordered by that list rather than by the container's own ordering.
    dispatchProps() {
      const props = (this.container && this.container.properties) || [];
      const result = [];
      for (const name of this.dispatchPropertyNames) {
        const match = props.find((p) => p.propertyName === name);
        if (match) result.push(match);
      }
      return result;
    },
    dirtyProps() {
      return this.dispatchProps.filter((prop) => this.isDirty(prop));
    },
    // Tank capacity in litres, or null when it is missing or unusable. Reads the
    // in-form edit first so the litre hints follow an unsaved capacity change
    // rather than the last saved one.
    capacityValue() {
      const props = (this.container && this.container.properties) || [];
      const cap = props.find((p) => p.propertyName === CAPACITY_PROPERTY);
      if (!cap) return null;

      const edited = this.edits[cap.containerPropId];
      const raw = edited != null && edited !== '' ? edited : cap.propValue;
      const n = parseFloat(raw);
      return isNaN(n) ? null : n;
    },
  },
  watch: {
    // A different site means different values; in-progress edits belong to the
    // site they were typed against, so they are dropped rather than carried over.
    containerId() {
      this.syncFromProps();
    },
  },
  methods: {
    // 'boolean' | 'number' | 'date' | 'text' -- which control renders the value.
    editorFor(prop) {
      switch (String((prop && prop.propertyType) || '').toUpperCase()) {
        case 'BOOLEAN': return 'boolean';
        case 'NUMBER':
        case 'DECIMAL': return 'number';
        case 'DATE': return 'date';
        default: return 'text';
      }
    },
    // propValue (always a string) to the type its editor expects.
    parseValue(prop) {
      const kind = this.editorFor(prop);
      const raw = prop ? prop.propValue : null;

      if (kind === 'boolean') {
        return TRUE_STRINGS.includes(String(raw == null ? '' : raw).trim().toLowerCase());
      }
      if (kind === 'number') {
        const n = parseFloat(raw);
        return isNaN(n) ? null : n;
      }
      if (kind === 'date') {
        // DATE rows are pre-formatted for display by containers/loadContainerStatus
        // ("2026-09-15, 10:30 a.m." in en-CA), so the calendar date is read off
        // the front of the string rather than parsed as a whole.
        const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(raw == null ? '' : raw).trim());
        if (!m) return null;
        return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
      }
      return raw == null ? '' : String(raw);
    },
    // Editor value back to the string the API stores.
    serializeValue(prop, value) {
      const kind = this.editorFor(prop);
      if (kind === 'boolean') return value ? '1' : '0';
      if (kind === 'date') {
        return value instanceof Date && !isNaN(value.getTime()) ? dateToString(value) : '';
      }
      if (value == null) return '';
      return String(value);
    },
    displayValue(prop) {
      const kind = this.editorFor(prop);
      if (kind === 'boolean') return this.parseValue(prop) ? 'Yes' : 'No';
      return prop.propValue;
    },
    isPercentProp(prop) {
      return !!prop && PERCENT_OF_CAPACITY_PROPERTIES.includes(prop.propertyName);
    },
    // What this property's percentage comes to against tank capacity, e.g. 95
    // against a 45000 L tank gives "= 42,750 L". Empty string when it cannot be
    // worked out, which hides the hint.
    capacityHint(prop) {
      if (!this.isPercentProp(prop)) return '';

      const capacity = this.capacityValue;
      if (capacity == null) return '';

      const pct = parseFloat(this.edits[prop.containerPropId]);
      if (isNaN(pct)) return '';

      const litres = (capacity * pct) / 100;
      if (!isFinite(litres)) return '';

      return '= ' + Math.round(litres).toLocaleString() + ' L';
    },
    syncFromProps() {
      const edits = {};
      const baseline = {};
      for (const prop of this.dispatchProps) {
        const parsed = this.parseValue(prop);
        edits[prop.containerPropId] = parsed;
        // Baseline holds the serialized form so comparison is like-for-like and
        // a reformat alone never counts as a change.
        baseline[prop.containerPropId] = this.serializeValue(prop, parsed);
      }
      this.edits = edits;
      this.baseline = baseline;
      this.saveError = null;
    },
    isDirty(prop) {
      if (!(prop.containerPropId in this.baseline)) return false;
      return this.serializeValue(prop, this.edits[prop.containerPropId])
        !== this.baseline[prop.containerPropId];
    },
    onValueChanged(prop, e) {
      this.edits[prop.containerPropId] = e.value;
    },
    // Looks up the property definition. Tolerates a name with no definition,
    // since DISPATCH_PROPERTIES is maintained by hand and a typo should show the
    // property read-only rather than throw.
    propertyDef(propName) {
      if (propName == undefined || propName == null) return null;
      const props = this.$store.getters["properties/getProperties"] || [];
      return props.find((x) => x.propertyName == propName) || null;
    },
    allowUserEdit(propName) {
      const prop = this.propertyDef(propName);
      if (!prop) return false;
      if (this.isOperator) return false;
      return !!prop.userEdit;
    },
    async saveChanges() {
      if (this.saving) return;
      const dirty = this.dirtyProps;
      if (!dirty.length) return;

      this.saving = true;
      this.saveError = null;

      // Still one request per changed property; they just go out together
      // instead of one after another. Each request catches its own rejection and
      // resolves to a result, because a Promise.all over raw dispatches would
      // reject on the first failure and lose the other properties' outcomes.
      const results = await Promise.all(
        dirty.map((prop) => {
          // A DATE is written back as yyyy-MM-dd, so any time the stored value
          // carried is dropped. DATE rows arrive pre-formatted with a time (see
          // the DATE branch of containers/loadContainerStatus), but none of the
          // properties configured here are DATE, so nothing loses a time today.
          // Revisit serializeValue before adding one whose time is meaningful.
          const propValue = this.serializeValue(prop, this.edits[prop.containerPropId]);
          const payload = {
            containerPropId: prop.containerPropId,
            containerId: this.container.containerId,
            propertyId: prop.propertyId,
            // The action's store commit keys off propertyName; the Details pane
            // omits it, which makes that commit throw.
            propertyName: prop.propertyName,
            propValue,
            // Thresholds are not edited here, but the action writes whatever it
            // is given -- omitting these would null them out on the saved record.
            marginal: prop.valueMarginal,
            critical: prop.valueCritical,
            highMarginal: prop.valueHighMarginal,
            highCritical: prop.valueHighCritical,
            propertySort: prop.propertySort,
          };

          return this.$store
            .dispatch("containers/updateContainerProperty", payload)
            .then(() => ({ prop, propValue, saved: true }))
            .catch((error) => ({
              prop,
              propValue,
              // Only a rejected PUT means the value did not save. The same
              // action then commits a local store sync that throws when this
              // container was never loaded into containersV2 -- that must not
              // read as a failure.
              saved: !(error && error.message === "Failed to send request."),
            }));
        })
      );

      // Applied after every request has settled, in the order the properties are
      // listed, so the message reads the same whichever request finished first.
      const failed = [];
      for (const result of results) {
        if (result.saved) {
          this.baseline[result.prop.containerPropId] = result.propValue;
        } else {
          failed.push(result.prop.propertyName);
        }
      }

      this.saving = false;

      // The toast is the visible confirmation; saveError stays as well, because
      // it names the properties that failed and does not time out.
      const label = dirty.length === 1 ? "property" : "properties";
      if (failed.length) {
        this.saveError = `Could not save: ${failed.join(", ")}.`;
        notify(`${failed.length} of ${dirty.length} ${label} failed to save.`, "error", 4000);
      } else {
        notify(`Saved ${dirty.length} ${label}.`, "success", 2000);
        this.$emit("recordUpdated");
      }
    },
    handleKeyDown(e) {
      if (e.event.key === 'e' || e.event.key === 'E') {
        e.event.preventDefault()
      }
    }
  },
};
</script>

<style scoped>
.dispatchPane {
  display: flex;
  flex-direction: column;
  max-height: 100%;
  min-height: 0;
  /* The same stack DevExtreme's .dx-widget uses, so this pane's own labels and
     values match the editors sitting inside them. The app sets no global
     font-family and body carries only dx-viewport (not
     dx-theme-material-typography), so plain divs would otherwise inherit the
     browser default while the widgets rendered in Roboto.
     Both names are needed: the bundled webfont is registered as RobotoFallback
     (see @font-face in themes/generated/theme.base.css), and bare "Roboto"
     resolves only when it happens to be installed locally. */
  font-family: Roboto, RobotoFallback, "Noto Kufi Arabic", Helvetica, Arial, sans-serif;
}

/* max-height rather than height: the pane sits in a DxBox item whose height does
   not always resolve to a definite value, and a percentage height against an
   auto-height parent computes to auto -- which is what stopped this scrolling.
   The px bound guarantees the overflow regardless. 520px keeps it inside the
   550px DxBox row in container-details.vue. */
.paneScroll {
  flex: 1 1 auto;
  min-height: 0;
  max-height: 520px;
  overflow-y: auto;
  overflow-x: hidden;
  /* Firefox */
  scrollbar-width: thin;
  scrollbar-color: darkorange rgba(255, 255, 255, 0.06);
}

/* Chromium/WebKit ignore scrollbar-color, so the accent is set again here. */
.paneScroll::-webkit-scrollbar {
  width: 10px;
}

.paneScroll::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.06);
  border-radius: 5px;
}

.paneScroll::-webkit-scrollbar-thumb {
  background: darkorange;
  border-radius: 5px;
}

.paneScroll::-webkit-scrollbar-thumb:hover {
  background: rgb(255, 140, 0);
}

.paneBody {
  padding: 18px 20px 4px 20px;
}

.emptyNote {
  font-size: 15px;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.4);
  padding: 10px 2px;
}

.fieldLabel {
  font-size: 13px;
  font-weight: 400;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.45);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dirtyMark {
  color: rgba(255, 115, 0, 0.95);
  margin-left: 5px;
  font-size: 17px;
  line-height: 1;
}

.readonlyVal {
  font-size: 15px;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.75);
  padding: 6px 0;
}

/* In flow at the end of the property list, not pinned to the pane, so it never
   gets pushed out of view. No margin-top: auto and no side padding -- .paneBody
   already supplies the gutter. */
.paneFooter {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 14px 0 6px 0;
}

.dirtyCount {
  font-size: 14px;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.4);
}

.saveError {
  font-size: 14px;
  font-weight: 300;
  color: #ff8a80;
}

.saveBtn {
  color: black;
  background-color: rgb(255, 115, 0);
  width: 110px;
}

.fieldRows {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Label left, control right, each row on its own card surface -- the same
   background, border and radius the tiles variant uses. */
.row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 14px;
  background-color: #26262c;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 8px;
  transition: border-color 0.15s ease;
  /* Hold every row at the height a text/number/date row needs, so a switch row
     or a read-only row does not come up short:
       19.3px  input line box (15px font x 1.2857 Material line-height)
       +  11px  input padding (6px top + 5px bottom, on .dx-texteditor-input)
       +  24px  row padding (12px top + 12px bottom)
       +   2px  row border (1px top + 1px bottom)
       =  57px  (rounded up from 56.3)
     The outlined field's ring is an inset box-shadow, so it adds no height, and
     the date-box / number-box buttons are sized off the input rather than past
     it. box-sizing: border-box is global (App.vue), so this is the final
     rendered height; align-items: center then centres shorter controls in it.
     Note this tracks the input's font size -- change that and the first term,
     and so this total, moves with it. */
  min-height: 57px;
}

.row.dirty {
  border-color: rgba(255, 115, 0, 0.65);
}

/* The property name, matched to the value beside it: Roboto 15px, Light. 300 is
   the lightest weight the theme actually bundles a face for (@font-face declares
   300/400/500/700), so a true Thin at 100 would only get synthesised. */
.rowLabel {
  font-size: 15px;
  font-weight: 300;
  letter-spacing: 0;
  text-transform: none;
  color: rgba(255, 255, 255, 0.7);
  min-width: 0;
  flex: 1 1 auto;
}

.row.dirty .rowLabel {
  color: rgba(255, 255, 255, 0.95);
}

/* Fixed control column so every row lines up down the right edge. Wide enough
   to hold the litre hint alongside the input on percent rows; on rows without
   one the input still sits flush right, so all inputs stay aligned. */
.rowControl {
  flex: 0 0 310px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
}

/* A fixed editor width means the input lines up whether or not a hint precedes
   it. The block editors fill it; text-align right-aligns the inline-block
   switch inside the same box. */
.editorWrap {
  width: 200px;
  text-align: right;
}

.capacityHint {
  font-size: 13px;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.4);
  white-space: nowrap;
}

/* The Material theme pads text editors to roughly 50px tall
   (.dx-editor-outlined .dx-texteditor-input gets `padding: 15px 16px 14px` at a
   16px font size). Nothing there is !important, so trimming the padding and
   font size here brings a row down to about 32px. :deep() is needed to reach
   inside DevExtreme's markup from a scoped block. */
.rowControl :deep(.dx-texteditor-input) {
  padding: 6px 10px 5px;
  font-size: 15px;
  /* Light, to match .rowLabel -- DevExtreme's .dx-widget sets 400. */
  font-weight: 300;
  min-height: 0;
}

/* The floating placeholder mirrors the input's padding, so it has to match or
   the text sits off-centre. */
.rowControl :deep(.dx-placeholder::before) {
  padding: 6px 10px 5px;
}

.rowControl :deep(.dx-texteditor) {
  min-height: 0;
}

/* Keeps the date-box drop-down and number-box spin buttons centred against the
   shorter input rather than stretching it back out. */
.rowControl :deep(.dx-texteditor-buttons-container) {
  align-items: center;
}

.row .readonlyVal {
  padding: 0;
}
</style>
